"use client"

import { useForm } from "@inertiajs/react"
import { Check, CheckCircle, Copy01 } from "@untitledui/icons"
import confetti from "canvas-confetti"
import { useEffect, useState } from "react"

import { Button } from "@/components/ui/button"
import { ButtonUtility } from "@/components/ui/button-utility"
import { FeaturedIcon } from "@/components/ui/featured-icon"
import { HintText, InputBase, Label, TextField } from "@/components/ui/input"
import { Dialog, Modal, ModalOverlay } from "@/components/ui/modal"
import { initialSettingsPasswordPath } from "@/routes"

interface PublishCelebrateModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  /** Relative path to the published playlist; the absolute URL is built client-side. */
  playlistPath: string
  /**
   * Whether to offer the challenge-free "set a password" path. Only for a
   * warm-start (claim) session — the seeded account's password is random and
   * unknown, so setting one secures the account and retires the claim link.
   */
  showPasswordSetup: boolean
}

export function PublishCelebrateModal({
  open,
  onOpenChange,
  playlistPath,
  showPasswordSetup,
}: PublishCelebrateModalProps) {
  // Fire confetti once when the modal opens. Honour reduced-motion: the
  // celebration is decorative, so it's skipped entirely.
  useEffect(() => {
    if (!open) return
    if (window.matchMedia?.("(prefers-reduced-motion: reduce)").matches) return

    void confetti({
      particleCount: 120,
      spread: 70,
      origin: { y: 0.3 },
      disableForReducedMotion: true,
    })
  }, [open])

  const [playlistUrl] = useState(() =>
    typeof window !== "undefined"
      ? `${window.location.origin}${playlistPath}`
      : playlistPath,
  )
  // The well clips the start of the URL, so a manual drag-select can't reach
  // it; a click selects the whole URL instead, hidden part included.
  const selectUrl = (e: React.MouseEvent<HTMLElement>) => {
    const selection = window.getSelection()
    if (!selection) return
    const range = document.createRange()
    range.selectNodeContents(e.currentTarget)
    selection.removeAllRanges()
    selection.addRange(range)
  }

  const [copied, setCopied] = useState(false)
  const copy = () => {
    void navigator.clipboard.writeText(playlistUrl).then(() => {
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    })
  }

  const { data, setData, post, processing, errors } = useForm({
    password: "",
    password_confirmation: "",
  })
  const setPassword = (e: React.FormEvent) => {
    e.preventDefault()
    post(initialSettingsPasswordPath())
  }

  return (
    <ModalOverlay isOpen={open} onOpenChange={onOpenChange} isDismissable>
      <Modal>
        <Dialog className="bg-primary mx-auto flex w-full max-w-md flex-col gap-6 rounded-2xl p-6 shadow-xl outline-hidden">
          <div className="flex flex-col items-center gap-3 text-center">
            <FeaturedIcon
              color="success"
              theme="light"
              size="lg"
              icon={CheckCircle}
            />
            <div className="flex flex-col gap-1">
              <h2 className="text-primary text-lg font-semibold">
                Your playlist is live
              </h2>
              <p className="text-tertiary text-sm">
                Anyone with the link can see it.
              </p>
            </div>
          </div>

          <div className="flex items-center justify-center gap-2">
            <code
              onClick={selectUrl}
              className="text-secondary bg-secondary border-secondary max-w-[250px] min-w-0 flex-1 cursor-text rounded-lg border px-3 py-2 text-sm"
            >
              <span className="flex justify-end overflow-hidden [mask-image:linear-gradient(to_right,transparent,black_1.5rem)]">
                <span className="whitespace-nowrap">{playlistUrl}</span>
              </span>
            </code>
            <ButtonUtility
              size="sm"
              color="secondary"
              icon={copied ? Check : Copy01}
              tooltip={copied ? "Copied" : "Copy link"}
              aria-label="Copy link"
              onClick={copy}
            />
          </div>

          {showPasswordSetup ? (
            <form
              onSubmit={setPassword}
              className="border-secondary flex flex-col gap-4 border-t pt-5"
            >
              <div className="flex flex-col gap-1">
                <p className="text-primary text-sm font-semibold">
                  Keep your account
                </p>
                <p className="text-tertiary text-sm">
                  Set a password so you can sign back in anytime.
                </p>
              </div>

              <TextField
                isRequired
                name="password"
                value={data.password}
                onChange={(v) => setData("password", v)}
                isInvalid={!!errors.password}
              >
                <Label>Password</Label>
                <InputBase
                  type="password"
                  size="md"
                  placeholder="At least 12 characters"
                />
                {errors.password && (
                  <HintText isInvalid>{errors.password}</HintText>
                )}
              </TextField>

              <TextField
                isRequired
                name="password_confirmation"
                value={data.password_confirmation}
                onChange={(v) => setData("password_confirmation", v)}
                isInvalid={!!errors.password_confirmation}
              >
                <Label>Confirm password</Label>
                <InputBase type="password" size="md" />
                {errors.password_confirmation && (
                  <HintText isInvalid>{errors.password_confirmation}</HintText>
                )}
              </TextField>

              <div className="flex justify-end gap-3">
                <Button
                  type="button"
                  color="secondary"
                  size="md"
                  onClick={() => onOpenChange(false)}
                >
                  Maybe later
                </Button>
                <Button
                  type="submit"
                  size="md"
                  isDisabled={processing}
                  isLoading={processing}
                >
                  Set password
                </Button>
              </div>
            </form>
          ) : (
            <div className="flex justify-end">
              <Button size="md" onClick={() => onOpenChange(false)}>
                Done
              </Button>
            </div>
          )}
        </Dialog>
      </Modal>
    </ModalOverlay>
  )
}
